import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { YStack } from 'tamagui';
import { topToastBus } from '@/lib/topToastBus';
import type { ShowTopToastOptions } from '@/lib/topToastTypes';

export type { ShowTopToastOptions } from '@/lib/topToastTypes';

const DEFAULT_TOP = 88;
const DEFAULT_MAX_W = 405;
const DEFAULT_Z = 11_000;
const DEFAULT_PADDING_X = 16;
const DEFAULT_ENTER_Y = -44;
const DEFAULT_EXIT_Y = -16;
const DEFAULT_ENTER_X = 0;
const DEFAULT_EXIT_X = 0;
const DEFAULT_ANIMATION = 'medium';

type TopToastState = ShowTopToastOptions & {
  key: number;
  maxWidth: number;
  top: number;
  zIndex: number;
  paddingX: number;
  horizontalAlign: 'center' | 'right';
  enterYOffset: number;
  exitYOffset: number;
  enterXOffset: number;
  exitXOffset: number;
  animation: string;
};

type TopToastContextValue = {
  showTopToast: (opts: ShowTopToastOptions) => void;
  dismissTopToast: () => void;
};

const TopToastContext = createContext<TopToastContextValue | null>(null);

export function useTopToast(): TopToastContextValue {
  const ctx = useContext(TopToastContext);
  if (!ctx) {
    throw new Error('useTopToast must be used within TopToastProvider');
  }
  return ctx;
}

export function useTopToastOptional(): TopToastContextValue | null {
  return useContext(TopToastContext);
}

function ToastInner({ toast }: { toast: TopToastState }) {
  const alignSelf = toast.horizontalAlign === 'right' ? 'flex-end' : 'center';
  return (
    <YStack
      key={toast.key}
      width="100%"
      maxWidth={toast.maxWidth}
      alignSelf={alignSelf}
      animation={toast.animation as 'quick' | 'medium' | 'slow' | 'lazy'}
      enterStyle={{
        y: toast.enterYOffset,
        x: toast.enterXOffset,
        opacity: 0,
      }}
      exitStyle={{
        y: toast.exitYOffset,
        x: toast.exitXOffset,
        opacity: 0,
      }}
    >
      {toast.content}
    </YStack>
  );
}

export function TopToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<TopToastState | null>(null);
  const keyRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismissTopToast = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const showTopToast = useCallback(
    (opts: ShowTopToastOptions) => {
      clearTimer();
      const k = ++keyRef.current;
      const maxWidth = opts.maxWidth ?? DEFAULT_MAX_W;
      const top = opts.top ?? DEFAULT_TOP;
      const zIndex = opts.zIndex ?? DEFAULT_Z;
      const paddingX = opts.paddingX ?? DEFAULT_PADDING_X;
      const horizontalAlign = opts.horizontalAlign ?? 'center';
      const enterYOffset = opts.enterYOffset ?? DEFAULT_ENTER_Y;
      const exitYOffset = opts.exitYOffset ?? DEFAULT_EXIT_Y;
      const enterXOffset = opts.enterXOffset ?? DEFAULT_ENTER_X;
      const exitXOffset = opts.exitXOffset ?? DEFAULT_EXIT_X;
      const animation = opts.animation ?? DEFAULT_ANIMATION;
      setToast({
        key: k,
        content: opts.content,
        maxWidth,
        top,
        zIndex,
        paddingX,
        horizontalAlign,
        enterYOffset,
        exitYOffset,
        enterXOffset,
        exitXOffset,
        animation,
        autoDismissMs: opts.autoDismissMs,
      });
      const ms = opts.autoDismissMs;
      if (typeof ms === 'number' && ms > 0) {
        timerRef.current = setTimeout(dismissTopToast, ms);
      }
    },
    [clearTimer, dismissTopToast],
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  const value = useMemo(
    () => ({ showTopToast, dismissTopToast }),
    [showTopToast, dismissTopToast],
  );

  useEffect(() => {
    topToastBus.bind({ showTopToast, dismissTopToast });
    return () => topToastBus.unbind();
  }, [showTopToast, dismissTopToast]);

  const portal =
    Platform.OS === 'web' && toast && typeof document !== 'undefined'
      ? (() => {
          try {
            const { createPortal } = require('react-dom') as {
              createPortal: (children: ReactNode, container: Element | DocumentFragment) => ReactNode;
            };
            return createPortal(
              <View
                style={{
                  position: 'fixed',
                  top: toast.top,
                  left: 0,
                  right: 0,
                  paddingLeft: toast.paddingX,
                  paddingRight: toast.paddingX,
                  alignItems: toast.horizontalAlign === 'right' ? 'flex-end' : 'center',
                  zIndex: toast.zIndex,
                  pointerEvents: 'box-none',
                }}
              >
                <ToastInner toast={toast} />
              </View>,
              document.body,
            );
          } catch {
            return null;
          }
        })()
      : null;

  const nativeLayer =
    toast && Platform.OS !== 'web' ? (
      <View
        style={[
          StyleSheet.absoluteFillObject,
          { zIndex: toast.zIndex, pointerEvents: 'box-none' },
        ]}
      >
        <View
          style={{
            paddingTop: toast.top,
            paddingLeft: toast.paddingX,
            paddingRight: toast.paddingX,
            alignItems: toast.horizontalAlign === 'right' ? 'flex-end' : 'center',
          }}
        >
          <ToastInner toast={toast} />
        </View>
      </View>
    ) : null;

  return (
    <TopToastContext.Provider value={value}>
      {Platform.OS === 'web' ? (
        <>
          {children}
          {portal}
        </>
      ) : (
        <View style={{ flex: 1 }}>
          {children}
          {nativeLayer}
        </View>
      )}
    </TopToastContext.Provider>
  );
}
